import { reactive, computed, watch } from 'vue'
import { DateRangeFilterType, DateRangeUnitOptions } from '@/enums'
import {
    addDays,
    addMonths,
    addWeeks,
    addYears, endOfDay, endOfWeek, endOfYear, formatISO, lastDayOfMonth, lightFormat,
    startOfDay, startOfMonth,
    startOfWeek, startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears
} from 'date-fns'
import { isValidIsoDate } from '@/helpers/form'

export class PostingFilter {
    constructor () {
        // Data
        this.data = reactive({
            advancedFilters: {
                dateRangeFilterType: DateRangeFilterType.DEFAULT,
                dateRangeAmount: 1,
                dateRangeUnit: DateRangeUnitOptions.WEEK,
                dateFrom: '',
                dateUntil: '',
            },
        })

        // Computed
        this.variables = computed(() => {
            const filterVariables = {}

            if (this.data.advancedFilters.dateFrom || this.data.advancedFilters.dateUntil) {
                if (this.data.advancedFilters.dateFrom && isValidIsoDate(this.data.advancedFilters.dateFrom)) filterVariables.filterDateFrom = formatISO(startOfDay(new Date(this.data.advancedFilters.dateFrom)))
                if (this.data.advancedFilters.dateUntil && isValidIsoDate(this.data.advancedFilters.dateUntil)) filterVariables.filterDateUntil = formatISO(endOfDay(new Date(this.data.advancedFilters.dateUntil)))
            }

            return filterVariables
        })

        // Watch
        watch(() => this.data.advancedFilters.dateRangeFilterType, (newValue, oldValue) => {
            switch (this.data.advancedFilters.dateRangeFilterType) {
                case DateRangeFilterType.DYNAMIC:
                    this.updateDateRange(new Date())
                    break
            }
        })

        watch(() => this.data.advancedFilters.dateRangeAmount, (newValue, oldValue) => {
            if (this.data.advancedFilters.dateRangeFilterType === DateRangeFilterType.DYNAMIC && this.data.advancedFilters.dateRangeUnit) this.updateDateRange(new Date(this.data.advancedFilters.dateFrom))
        })

        watch(() => this.data.advancedFilters.dateRangeUnit, (newValue, oldValue) => {
            if (this.data.advancedFilters.dateRangeFilterType === DateRangeFilterType.DYNAMIC) {
                if (newValue) {
                    this.updateDateRange(new Date())
                } else {
                    this.data.advancedFilters.dateFrom = ''
                    this.data.advancedFilters.dateUntil = ''
                }
            }
        })

        this.data.advancedFilters.dateRangeFilterType = DateRangeFilterType.DYNAMIC // TODO: Check.
    }

    clearDateFilter () {
        this.data.advancedFilters.dateRangeFilterType = DateRangeFilterType.DEFAULT
        this.data.advancedFilters.dateFrom = ''
        this.data.advancedFilters.dateUntil = ''
        this.data.advancedFilters.dateRangeAmount = 1
        this.data.advancedFilters.dateRangeUnit = DateRangeUnitOptions.WEEK
    }

    getAddDate (date, amount) {
        const fn = {
            [DateRangeUnitOptions.DAY]: (date, numOfDays) => addDays(date, numOfDays),
            [DateRangeUnitOptions.WEEK]: (date, numOfWeeks) => addWeeks(date, numOfWeeks),
            [DateRangeUnitOptions.MONTH]: (date, numOfMonths) => addMonths(date, numOfMonths),
            [DateRangeUnitOptions.YEAR]: (date, numOfYears) => addYears(date, numOfYears),
        }
        return fn[this.data.advancedFilters.dateRangeUnit](date, amount)
    }

    getSubDate (date, amount) {
        const fn = {
            [DateRangeUnitOptions.DAY]: (date, numOfDays) => subDays(date, numOfDays),
            [DateRangeUnitOptions.WEEK]: (date, numOfWeeks) => subWeeks(date, numOfWeeks),
            [DateRangeUnitOptions.MONTH]: (date, numOfMonths) => subMonths(date, numOfMonths),
            [DateRangeUnitOptions.YEAR]: (date, numOfYears) => subYears(date, numOfYears),
        }
        return fn[this.data.advancedFilters.dateRangeUnit](date, amount)
    }

    getDateStartBy (date) {
        const fn = {
            [DateRangeUnitOptions.DAY]: date => startOfDay(date),
            [DateRangeUnitOptions.WEEK]: date => startOfWeek(date, { weekStartsOn: 1 }),
            [DateRangeUnitOptions.MONTH]: date => startOfMonth(date),
            [DateRangeUnitOptions.YEAR]: date => startOfYear(date),
        }
        return fn[this.data.advancedFilters.dateRangeUnit](date)
    }

    getDateEndBy (date) {
        const fn = {
            [DateRangeUnitOptions.DAY]: date => endOfDay(date),
            [DateRangeUnitOptions.WEEK]: date => endOfWeek(date, { weekStartsOn: 1 }),
            [DateRangeUnitOptions.MONTH]: date => lastDayOfMonth(date),
            [DateRangeUnitOptions.YEAR]: date => endOfYear(date),
        }
        return fn[this.data.advancedFilters.dateRangeUnit](date)
    }

    shiftDateRangeEarlier () {
        const dateFrom = this.getDateStartBy(this.getSubDate(new Date(this.data.advancedFilters.dateFrom), this.data.advancedFilters.dateRangeAmount))
        this.updateDateRange(dateFrom)
    }

    shiftDateRangeLater () {
        const dateFrom = this.getDateStartBy(this.getAddDate(new Date(this.data.advancedFilters.dateFrom), this.data.advancedFilters.dateRangeAmount))
        this.updateDateRange(dateFrom)
    }

    updateDateRange (referenceDate) {
        const dateFrom = this.getDateStartBy(referenceDate)
        const dateUntil = this.getDateEndBy(this.getAddDate(dateFrom, this.data.advancedFilters.dateRangeAmount - 1))
        this.data.advancedFilters.dateFrom = lightFormat(dateFrom, 'yyyy-MM-dd')
        this.data.advancedFilters.dateUntil = lightFormat(dateUntil, 'yyyy-MM-dd')
    }
}
